import {
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    BellIcon,
    BoltIcon,
    BookOpenIcon,
    CalendarIcon,
    ChartBarIcon,
    ChatBubbleLeftRightIcon,
    ChevronDownIcon,
    Cog6ToothIcon,
    HomeIcon,
    MoonIcon,
    PlusCircleIcon,
    SunIcon,
    UserCircleIcon,
    UserGroupIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import apiService from '../services/api';
import { canAccessPage, UserRole } from '../utils/permissions';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notificationsDropdownOpen, setNotificationsDropdownOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const { user, gym, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsDropdownRef = useRef<HTMLDivElement>(null);

  // Load notification count and recent activity
  const loadNotificationCount = async () => {
    try {
      const requests = await apiService.getMemberRequests();
      const pendingCount = requests.filter(request => request.status === 'pending').length;
      setNotificationCount(pendingCount);
    } catch (error) {
      console.error('Failed to load notification count:', error);
    }
  };

  const loadRecentActivity = async () => {
    try {
      setLoadingActivity(true);
      const activity = await apiService.getRecentActivity();
      setRecentActivity(activity);
    } catch (error) {
      console.error('Failed to load recent activity:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
      if (notificationsDropdownRef.current && !notificationsDropdownRef.current.contains(event.target as Node)) {
        setNotificationsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load notification count and activity on mount and when location changes
  useEffect(() => {
    if (user && gym) {
      loadNotificationCount();
      loadRecentActivity();
    }
  }, [user, gym, location.pathname]);

  // Load activity when notifications dropdown opens
  useEffect(() => {
    if (notificationsDropdownOpen && user && gym) {
      loadRecentActivity();
    }
  }, [notificationsDropdownOpen, user, gym]);

  // Listen for notification updates from other components
  useEffect(() => {
    const handleNotificationUpdate = () => {
      loadNotificationCount();
    };

    window.addEventListener('notificationUpdate', handleNotificationUpdate);
    return () => {
      window.removeEventListener('notificationUpdate', handleNotificationUpdate);
    };
  }, []);

  // Get user role from membership_type
  const userRole = (user?.membership_type as UserRole) || 'viewer';
  
  const allNavigationItems = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, page: 'dashboard' },
    { name: 'Classes', href: '/classes', icon: CalendarIcon, page: 'classes' },
    { name: 'Workouts', href: '/workouts', icon: BoltIcon, page: 'workouts' },
    { name: 'Members', href: '/members', icon: UserGroupIcon, page: 'members' },
    { name: 'Manage Employees', href: '/employee-management', icon: UserGroupIcon, page: 'employee-management' },
    { name: 'Gym Settings', href: '/gym-settings', icon: Cog6ToothIcon, page: 'gym-settings' },
    { name: 'Communication', href: '/communication', icon: ChatBubbleLeftRightIcon, page: 'communication' },
    { name: 'Reports', href: '/reports', icon: ChartBarIcon, page: 'reports' },
    { name: 'Handbook', href: '/handbook', icon: BookOpenIcon, page: 'handbook' },
    // Billing removed - CEO manages all subscriptions
    // Only show gym registration for CEO
    ...(user?.is_ceo ? [{ name: 'Register New Gym', href: '/gym-registration', icon: PlusCircleIcon, page: 'gym-registration' }] : []),
  ];

  // Filter navigation items based on user permissions
  const navigation = allNavigationItems.filter(item => 
    canAccessPage(userRole, item.page)
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={clsx(
      "min-h-screen transition-colors duration-300",
      theme === 'dark' 
        ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" 
        : "bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100"
    )}>

      {/* Mobile sidebar */}
      <div className={clsx(
        'fixed inset-0 z-50 lg:hidden',
        sidebarOpen ? 'block' : 'hidden'
      )}>
        <div className={clsx(
          "fixed inset-0 backdrop-blur-sm transition-colors",
          theme === 'dark' ? "bg-black/50" : "bg-black/30"
        )} onClick={() => setSidebarOpen(false)} />
        <div className={clsx(
          "fixed inset-y-0 left-0 flex w-64 flex-col backdrop-blur-xl border-r transition-colors",
          theme === 'dark' 
            ? "bg-slate-800/80 border-slate-700/50" 
            : "bg-white/90 border-gray-200/50"
        )}>
          <div className={clsx(
            "flex h-16 items-center justify-between px-4 border-b transition-colors",
            theme === 'dark' ? "border-slate-700/50" : "border-gray-200/50"
          )}>
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {/* Gym Logo */}
              {gym?.logo_url && (
                <img 
                  src={gym.logo_url} 
                  alt={`${gym.name} Logo`}
                  className="w-8 h-8 object-cover rounded-lg flex-shrink-0 ring-2 ring-offset-2 ring-offset-transparent ring-gray-300/20"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <div className="flex-1 min-w-0">
                <h1 className={clsx(
                  "text-lg font-semibold break-words leading-tight transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>
                  {gym ? gym.name : 'Central Core+ Admin'}
                </h1>
              </div>
            </div>
            <button
              type="button"
              className={clsx(
                "ml-2 transition-colors",
                theme === 'dark' ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
              )}
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                    isActive
                      ? theme === 'dark'
                        ? 'bg-gradient-to-r from-orange-500/20 to-blue-500/20 text-white shadow-lg backdrop-blur-sm border border-orange-400/30'
                        : 'bg-gradient-to-r from-orange-500/10 to-blue-500/10 text-gray-900 shadow-md border border-orange-300/30'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:bg-gradient-to-r hover:from-orange-500/10 hover:to-blue-500/10 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100/80 hover:text-gray-900'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon
                    className={clsx(
                      'mr-3 h-5 w-5 flex-shrink-0 transition-colors',
                      isActive 
                        ? theme === 'dark' ? 'text-white' : 'text-gray-900'
                        : theme === 'dark' ? 'text-gray-400 group-hover:text-gray-200' : 'text-gray-500 group-hover:text-gray-700'
                    )}
                  />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className={clsx(
          "flex flex-col flex-grow backdrop-blur-xl border-r transition-colors",
          theme === 'dark' 
            ? "bg-slate-800/80 border-slate-700/50" 
            : "bg-white/90 border-gray-200/50"
        )}>
          <div className={clsx(
            "flex h-16 items-center px-4 border-b transition-colors",
            theme === 'dark' ? "border-slate-700/50" : "border-gray-200/50"
          )}>
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              {/* Gym Logo */}
              {gym?.logo_url && (
                <img 
                  src={gym.logo_url} 
                  alt={`${gym.name} Logo`}
                  className="w-8 h-8 object-cover rounded-lg flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <div className="flex-1 min-w-0">
                <h1 className={clsx(
                  "text-lg font-semibold break-words leading-tight transition-colors",
                  theme === 'dark' ? "text-white" : "text-gray-900"
                )}>
                  {gym ? gym.name : 'Central Core+ Admin'}
                </h1>
              </div>
            </div>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                    isActive
                      ? theme === 'dark'
                        ? 'bg-gradient-to-r from-orange-500/20 to-blue-500/20 text-white shadow-lg backdrop-blur-sm border border-orange-400/30'
                        : 'bg-gradient-to-r from-orange-500/10 to-blue-500/10 text-gray-900 shadow-md border border-orange-300/30'
                      : theme === 'dark'
                        ? 'text-gray-300 hover:bg-gradient-to-r hover:from-orange-500/10 hover:to-blue-500/10 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100/80 hover:text-gray-900'
                  )}
                >
                  <item.icon
                    className={clsx(
                      'mr-3 h-5 w-5 flex-shrink-0 transition-colors',
                      isActive 
                        ? theme === 'dark' ? 'text-white' : 'text-gray-900'
                        : theme === 'dark' ? 'text-gray-400 group-hover:text-gray-200' : 'text-gray-500 group-hover:text-gray-700'
                    )}
                  />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className={clsx(
          "sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b backdrop-blur-xl px-4 shadow-lg sm:gap-x-6 sm:px-6 lg:px-8 transition-colors",
          theme === 'dark'
            ? "border-slate-700/50 bg-slate-800/80"
            : "border-gray-200/50 bg-white/90"
        )}>
          <button
            type="button"
            className={clsx(
              "lg:hidden -m-2.5 p-2.5 transition-colors",
              theme === 'dark' ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
            )}
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Gym Name & Logo - Mobile */}
          <div className="lg:hidden flex-1 text-center flex items-center justify-center space-x-2">
            {/* Gym Logo */}
            {gym?.logo_url && (
              <img 
                src={gym.logo_url} 
                alt={`${gym.name} Logo`}
                className="w-6 h-6 object-cover rounded flex-shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <h2 className={clsx(
              "text-lg font-semibold break-words leading-tight transition-colors",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>
              {gym ? gym.name : 'Central Core+ Admin'}
            </h2>
          </div>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Notifications Dropdown */}
              <div className="relative" ref={notificationsDropdownRef}>
                <button
                  onClick={() => {
                    setNotificationsDropdownOpen(!notificationsDropdownOpen);
                    if (!notificationsDropdownOpen) {
                      loadRecentActivity();
                    }
                  }}
                  className={clsx(
                    "relative p-2 transition-colors",
                    notificationCount > 0 
                      ? theme === 'dark' ? 'text-white hover:text-gray-200' : 'text-gray-900 hover:text-gray-700'
                      : theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                  )}
                  title="Recent Activity"
                >
                  <BellIcon className={`h-6 w-6 ${notificationCount > 0 ? 'animate-pulse' : ''}`} />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-bounce">
                      {notificationCount > 99 ? '99+' : notificationCount}
                    </span>
                  )}
                </button>

                {/* Notifications Dropdown Menu */}
                {notificationsDropdownOpen && (
                  <div className={clsx(
                    "absolute right-0 mt-2 w-96 backdrop-blur-xl rounded-xl shadow-2xl border z-50 transition-colors max-h-[600px] flex flex-col",
                    theme === 'dark'
                      ? "bg-slate-800/95 border-slate-700/50"
                      : "bg-white/95 border-gray-200/50"
                  )}>
                    {/* Header */}
                    <div className={clsx(
                      "px-4 py-3 border-b transition-colors flex items-center justify-between",
                      theme === 'dark' ? "border-slate-700/50" : "border-gray-200/50"
                    )}>
                      <h3 className={clsx(
                        "text-lg font-semibold transition-colors",
                        theme === 'dark' ? "text-white" : "text-gray-900"
                      )}>
                        Recent Activity
                      </h3>
                      <button
                        onClick={() => setNotificationsDropdownOpen(false)}
                        className={clsx(
                          "transition-colors",
                          theme === 'dark' ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"
                        )}
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Activity List */}
                    <div className="overflow-y-auto flex-1">
                      {loadingActivity ? (
                        <div className={clsx(
                          "px-4 py-8 text-center transition-colors",
                          theme === 'dark' ? "text-gray-400" : "text-gray-500"
                        )}>
                          Loading...
                        </div>
                      ) : recentActivity.length === 0 ? (
                        <div className={clsx(
                          "px-4 py-8 text-center transition-colors",
                          theme === 'dark' ? "text-gray-400" : "text-gray-500"
                        )}>
                          No recent activity
                        </div>
                      ) : (
                        <div className="py-2">
                          {recentActivity.map((activity, index) => (
                            <div
                              key={index}
                              className={clsx(
                                "px-4 py-3 border-b transition-colors cursor-pointer hover:bg-opacity-50",
                                theme === 'dark'
                                  ? "border-slate-700/50 hover:bg-white/5"
                                  : "border-gray-200/50 hover:bg-gray-50"
                              )}
                              onClick={() => {
                                // Navigate based on activity type
                                if (activity.type === 'member_request') {
                                  navigate('/members?tab=requests');
                                } else if (activity.type === 'new_member') {
                                  navigate('/members');
                                } else if (activity.type === 'class_booking' || activity.type === 'class_created') {
                                  navigate('/classes');
                                } else if (activity.type === 'workout_completion') {
                                  navigate('/workouts');
                                } else if (activity.type === 'communication') {
                                  navigate('/communication');
                                } else if (activity.type === 'billing') {
                                  navigate('/billing');
                                }
                                setNotificationsDropdownOpen(false);
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className={clsx(
                                  "flex-shrink-0 w-2 h-2 rounded-full mt-2",
                                  activity.type === 'member_request' ? 'bg-orange-500' :
                                  activity.type === 'new_member' ? 'bg-green-500' :
                                  activity.type === 'class_booking' || activity.type === 'class_created' ? 'bg-blue-500' :
                                  activity.type === 'workout_completion' ? 'bg-purple-500' :
                                  activity.type === 'communication' ? 'bg-indigo-500' :
                                  activity.type === 'billing' ? 'bg-yellow-500' :
                                  'bg-gray-500'
                                )} />
                                <div className="flex-1 min-w-0">
                                  <p className={clsx(
                                    "text-sm font-medium transition-colors",
                                    theme === 'dark' ? "text-white" : "text-gray-900"
                                  )}>
                                    {activity.message}
                                  </p>
                                  <p className={clsx(
                                    "text-xs mt-1 transition-colors",
                                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                                  )}>
                                    {activity.timestamp}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className={clsx(
                      "px-4 py-3 border-t transition-colors",
                      theme === 'dark' ? "border-slate-700/50" : "border-gray-200/50"
                    )}>
                      <button
                        onClick={() => {
                          navigate('/members?tab=requests');
                          setNotificationsDropdownOpen(false);
                        }}
                        className={clsx(
                          "w-full text-sm font-medium transition-colors",
                          theme === 'dark'
                            ? "text-orange-400 hover:text-orange-300"
                            : "text-orange-600 hover:text-orange-700"
                        )}
                      >
                        View All Activity
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Profile dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className={clsx(
                    "flex items-center gap-x-2 text-sm transition-colors",
                    theme === 'dark' ? "text-white hover:text-gray-200" : "text-gray-900 hover:text-gray-700"
                  )}
                >
                  <UserCircleIcon className={clsx(
                    "h-8 w-8 transition-colors",
                    theme === 'dark' ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
                  )} />
                  <ChevronDownIcon className="h-4 w-4" />
                </button>

                {/* Dropdown menu */}
                {profileDropdownOpen && (
                  <div className={clsx(
                    "absolute right-0 mt-2 w-64 backdrop-blur-xl rounded-xl shadow-2xl border py-2 z-50 transition-colors",
                    theme === 'dark'
                      ? "bg-slate-800/95 border-slate-700/50"
                      : "bg-white/95 border-gray-200/50"
                  )}>
                    {/* User info */}
                    <div className={clsx(
                      "px-4 py-3 border-b transition-colors",
                      theme === 'dark' ? "border-slate-700/50" : "border-gray-200/50"
                    )}>
                      <div className="flex items-center gap-x-3">
                        <UserCircleIcon className={clsx(
                          "h-10 w-10",
                          theme === 'dark' ? "text-gray-300" : "text-gray-600"
                        )} />
                        <div className="flex-1 min-w-0">
                          <p className={clsx(
                            "text-sm font-medium truncate transition-colors",
                            theme === 'dark' ? "text-white" : "text-gray-900"
                          )}>
                            {user?.first_name} {user?.last_name}
                          </p>
                          <p className={clsx(
                            "text-xs truncate transition-colors",
                            theme === 'dark' ? "text-gray-300" : "text-gray-600"
                          )}>
                            {user?.email}
                          </p>
                          {user?.is_ceo && (
                            <span className={clsx(
                              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border mt-1 transition-colors",
                              theme === 'dark'
                                ? "bg-orange-500/20 text-orange-200 border-orange-400/30"
                                : "bg-orange-100 text-orange-700 border-orange-300/50"
                            )}>
                              CEO
                            </span>
                          )}
                          {user?.is_gym_owner && !user?.is_ceo && (
                            <span className={clsx(
                              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border mt-1 transition-colors",
                              theme === 'dark'
                                ? "bg-blue-500/20 text-blue-200 border-blue-400/30"
                                : "bg-blue-100 text-blue-700 border-blue-300/50"
                            )}>
                              Gym Owner
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Menu items */}
                    <div className="py-2">
                      <button className={clsx(
                        "w-full px-4 py-2 text-sm transition-colors text-left flex items-center gap-x-2",
                        theme === 'dark'
                          ? "text-gray-300 hover:bg-gradient-to-r hover:from-orange-500/10 hover:to-blue-500/10 hover:text-white"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      )}>
                        <UserCircleIcon className="h-4 w-4" />
                        Edit Profile
                      </button>
                      <button className={clsx(
                        "w-full px-4 py-2 text-sm transition-colors text-left flex items-center gap-x-2",
                        theme === 'dark'
                          ? "text-gray-300 hover:bg-gradient-to-r hover:from-orange-500/10 hover:to-blue-500/10 hover:text-white"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      )}>
                        <Cog6ToothIcon className="h-4 w-4" />
                        Account Settings
                      </button>
        <div className={clsx(
          "w-full px-4 py-2 text-sm transition-colors flex items-center justify-between",
          theme === 'dark'
            ? "text-gray-300"
            : "text-gray-700"
        )}>
          <div className="flex items-center gap-x-2">
            {theme === 'dark' ? (
              <>
                <MoonIcon className="h-4 w-4" />
                <span>Dark Mode</span>
              </>
            ) : (
              <>
                <SunIcon className="h-4 w-4" />
                <span>Light Mode</span>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className={clsx(
              "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2",
              theme === 'dark'
                ? "bg-orange-500 focus:ring-orange-500"
                : "bg-blue-500 focus:ring-blue-500"
            )}
            role="switch"
            aria-checked={theme === 'dark'}
          >
            <span
              className={clsx(
                "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                theme === 'dark' ? "translate-x-5" : "translate-x-0"
              )}
            />
          </button>
        </div>
                      <button className={clsx(
                        "w-full px-4 py-2 text-sm transition-colors text-left flex items-center gap-x-2",
                        theme === 'dark'
                          ? "text-gray-300 hover:bg-gradient-to-r hover:from-orange-500/10 hover:to-blue-500/10 hover:text-white"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      )}>
                        Help & Support
                      </button>
                      <div className={clsx(
                        "mx-2 my-1 border-t transition-colors",
                        theme === 'dark' ? "border-slate-700/50" : "border-gray-200/50"
                      )} />
                      <button 
                        onClick={handleLogout}
                        className={clsx(
                          "w-full px-4 py-2 text-sm transition-colors text-left flex items-center gap-x-2",
                          theme === 'dark'
                            ? "text-red-300 hover:bg-red-500/20 hover:text-red-200"
                            : "text-red-600 hover:bg-red-50 hover:text-red-700"
                        )}
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6 relative z-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 